import React, { useState, useEffect } from 'react'; // Fixed: Import useEffect
import { Link, useNavigate } from 'react-router-dom';
import { buildMenuData } from '../data/menuData';
import { ChevronDown, Menu, X, Search, Sparkles, Zap, TrendingUp } from 'lucide-react';
import SubMenu from './SubMenu';
import slugify from '../utils/slugify';
import { useTools } from '../context/ToolContext';

const Header = () => {
  const { toolCategories, loading } = useTools();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPromoAd, setShowPromoAd] = useState(true);
  const navigate = useNavigate();

  // ❌ REMOVE THIS: Early return causes hook order issues
  // if (loading) return null;

  // ✅ Move useEffect BEFORE any conditional returns
  useEffect(() => {
    const isClosed = localStorage.getItem('header_promo_closed');
    if (isClosed) {
      setShowPromoAd(false);
    }
  }, []);

  // ✅ Now safe to return loading state
  if (loading) {
    return (
      <header className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50 shadow-sm">
        <div className="max-w-9xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          </div>
          <div className="w-64 h-10 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
        </div>
      </header>
    );
  }

  const menuData = buildMenuData(toolCategories);

  const handleCategorySelect = (label: string) => {
    const slug = slugify(label);
    setMobileOpen(false);
    navigate(`/categories/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const query = encodeURIComponent(searchQuery.trim());
      navigate(`/search?q=${query}`);
      setSearchQuery('');
      setMobileOpen(false);
    }
  };

  // Close promo ad
  const closePromoAd = () => {
    setShowPromoAd(false);
    localStorage.setItem('header_promo_closed', 'true');
  };

  return (
    <>
      {/* Promotional Header Banner (Optional) */}
      {showPromoAd && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="max-w-9xl mx-auto px-4 py-1.5 flex items-center justify-center relative">
            <div className="flex items-center gap-2 text-sm">
              <Zap className="w-4 h-4" />
              <span className="font-medium">🔥 Trending: AI Video Tools 2024</span>
              <Link 
                to="/trending/video-ai" 
                className="underline hover:no-underline ml-2 font-semibold"
              >
                Explore Now →
              </Link>
            </div>
            <button 
              onClick={closePromoAd}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white"
              aria-label="Close banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50 shadow-sm">
        <div className="max-w-9xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo Section */}
            <div className="flex items-center gap-8">
              <Link 
                to="/" 
                className="flex items-center gap-3 group"
                onClick={() => setMobileOpen(false)}
              >
                <img
                  src="/image/tooliq.webp"
                  alt="TooliQ Logo"
                  className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
                />
                <div className="hidden sm:block">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                      TooliQ
                    </span>
                    <Sparkles className="w-4 h-4 text-purple-500" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                    5,000+ AI Tools
                  </p>
                </div>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-1">
                {menuData.map((menu, idx) => (
                  <div key={idx} className="relative group">
                    {menu.submenu && menu.submenu.length > 0 ? (
                      <>
                        <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 group">
                          {menu.label}
                          <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                        </button>
                        <div className="absolute left-0 top-full pt-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                          <SubMenu items={menu.submenu} onSelect={handleCategorySelect} />
                        </div>
                      </>
                    ) : (
                      <Link
                        to={menu.link || '/'}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-all duration-200"
                      >
                        {menu.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Search Bar & Actions */}
            <div className="flex items-center gap-4">
              {/* Ad Slot: Header Mini Promo (Optional) */}
              {process.env.NODE_ENV === 'development' && (
                <div className="hidden lg:block">
                  <div className="text-xs text-gray-600 dark:text-gray-400 border-l border-gray-300 dark:border-gray-700 pl-4">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-green-500" />
                      <span>Sponsored AI Tool</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Bar - Desktop */}
              <form onSubmit={handleSearch} className="hidden md:block">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search AI tools or categories..."
                    className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
              </form>

              {/* Submit Tool Button (Revenue Source) */}
              <Link
                to="/submit-tool"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
              >
                <Sparkles className="w-4 h-4" />
                Submit Tool
              </Link>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg">
            <div className="px-4 py-3">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search AI tools or categories..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </form>

              {/* Submit Tool Button in Mobile */}
              <Link
                to="/submit-tool"
                onClick={() => setMobileOpen(false)}
                className="mb-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-sm"
              >
                <Sparkles className="w-4 h-4" />
                Submit AI Tool
              </Link>

              {/* Mobile Navigation Links */}
              <nav className="space-y-1">
                {menuData.map((menu, idx) => (
                  <div key={idx} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                    {menu.submenu && menu.submenu.length > 0 ? (
                      <details className="group">
                        <summary className="flex justify-between items-center py-3 px-2 text-gray-700 dark:text-gray-300 font-medium cursor-pointer list-none">
                          <span>{menu.label}</span>
                          <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                        </summary>
                        <div className="ml-4 pb-2 space-y-1">
                          {menu.submenu.map((item, subIdx) => (
                            <button
                              key={subIdx}
                              onClick={() => handleCategorySelect(item.label)}
                              className="block w-full text-left py-2 px-3 text-sm text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <Link
                        to={menu.link || '/'}
                        onClick={() => setMobileOpen(false)}
                        className="block py-3 px-2 text-gray-700 dark:text-gray-300 font-medium hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        {menu.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;